import React, { useState, useRef } from 'react';
import { Upload, AlertCircle, Loader2 } from 'lucide-react';
import { useProductStore } from '../../stores/productStore';
import { importFromZip } from '../../utils/import/zipImporter';
import { LoadingOverlay } from '../common/LoadingOverlay';

export function ImportFromZip() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addProduct = useProductStore(state => state.addProduct);
  const updateProduct = useProductStore(state => state.updateProduct);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setProgress(null);

    try {
      const { success, products, errors } = await importFromZip(file);

      if (!success) {
        setError(errors.length > 0 ? errors.join('\n') : '商品データの読み込みに失敗しました');
        return;
      }

      const failures: string[] = [];

      // Create products sequentially so image uploads don't overwhelm the
      // browser/Supabase, and so a single failure doesn't abort the rest.
      for (let i = 0; i < products.length; i++) {
        setProgress({ current: i + 1, total: products.length });
        try {
          // addProduct() with no template keeps the imported values intact.
          const newProduct = await addProduct();
          await updateProduct({
            ...newProduct,
            ...products[i],
            id: newProduct.id,
            folderId: newProduct.folderId,
            serialNumber: newProduct.serialNumber
          });
        } catch (err) {
          failures.push(`商品${i + 1}: ${err instanceof Error ? err.message : '作成に失敗しました'}`);
        }
      }

      if (failures.length > 0) {
        setError(
          `${products.length}件中${products.length - failures.length}件を読み込みました。\n` +
            failures.join('\n')
        );
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : '不明なエラーが発生しました');
    } finally {
      setIsProcessing(false);
      setProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="relative w-full sm:w-auto">
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isProcessing}
        className={`flex items-center justify-center gap-2 px-4 py-3 h-18 rounded-md text-sm font-medium transition-colors w-full sm:w-auto ${
          isProcessing
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'text-white bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={handleFileSelect}
          onClick={e => {
            (e.target as HTMLInputElement).value = '';
          }}
        />
        {isProcessing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Upload className="w-5 h-5" />
        )}
        <span>画像とCSVを読み込み</span>
      </button>

      {error && (
        <div className="absolute z-20 mt-2 left-0 right-0 sm:right-auto sm:min-w-[20rem] p-3 bg-red-50 text-red-600 text-sm rounded-md shadow-lg border border-red-200 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="whitespace-pre-wrap">{error}</div>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-xs text-red-500 hover:text-red-700 underline"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {isProcessing && (
        <LoadingOverlay
          message={
            progress
              ? `商品データを読み込んでいます (${progress.current}/${progress.total})...`
              : '商品データを読み込んでいます。今しばらくお待ちください...'
          }
        />
      )}
    </div>
  );
}
